// Customer.io CDP provider: a fan-out SINK for the analytics layer.
//
// Mirrors every typed event from `src/events` into Customer.io so campaigns
// and segments can react to product behaviour (first trade, boost, gift,
// onboarding steps, …). Delivery is through the composite (see
// ../../composite.ts): application code only emits typed events, the
// composite fans them out — no per-callsite vendor calls.
//
// Identity is deliberately NOT handled here. `providers/customerio-provider.tsx`
// owns the identify/reset lifecycle on the SAME shared client (see ./client),
// so events tracked here attach to the current profile automatically —
// anonymous before login, the Morfi user id after.
//
// Customer.io is not a feature-flag vendor; the gate/config/experiment reads
// are inert and the composite routes those to the primary (Statsig).

import type { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import type { AnalyticsConfig, AnalyticsProvider, ExperimentResult } from '../../types';
import { isCustomerIoNetwork, loadCustomerIoCdp } from './client';

const swallow = (): void => undefined;

export class CustomerIoAnalyticsProvider implements AnalyticsProvider {
  private readonly writeKey: string;
  private analytics: AnalyticsBrowser | null = null;
  private initialized = false;

  constructor(writeKey: string) {
    this.writeKey = writeKey;
  }

  // Deliberately does NOT load the client. Loading is itself a write (the SDK
  // fires an automatic page call) and the sink is constructed before the
  // active network is known, so the first eligible event loads it instead.
  // The flag preserves the contract that nothing is tracked before this runs.
  async initialize(_config: AnalyticsConfig): Promise<void> {
    this.initialized = true;
  }

  checkGate(_name: string): boolean {
    return false;
  }

  getConfig(_name: string): Record<string, unknown> {
    return {};
  }

  getExperiment(_name: string): ExperimentResult {
    return { value: null, ruleID: '' };
  }

  trackEvent(name: string, properties: Record<string, unknown>): void {
    // The event carries the network it was emitted on (stamped in
    // ../../index.ts), and that value — not the one at construction time —
    // decides. A user can toggle network mid-session, so this is re-checked
    // per event rather than latched once.
    if (!this.initialized) return;
    const network = properties.network;
    if (typeof network !== 'string' || !isCustomerIoNetwork(network)) return;
    // Checked BEFORE the cached client is reused: once loaded on devnet the
    // instance persists, so latching on it would let a subsequent toggle to a
    // non-production network start sending again.
    const analytics = this.analytics ?? loadCustomerIoCdp(this.writeKey, network);
    if (analytics === null) return;
    this.analytics = analytics;
    void analytics.track(name, properties).catch(swallow);
  }

  identifyUser(_userId: string, _attributes: Record<string, unknown>): void {
    // No-op by design: CustomerIoProvider identifies (and resets on logout)
    // the shared client from auth-store truth. Duplicating it here would race
    // that lifecycle and double-write profile attributes.
  }

  shutdown(): void {
    this.initialized = false;
    this.analytics = null;
  }
}

export function createCustomerIoProvider(writeKey: string): AnalyticsProvider {
  return new CustomerIoAnalyticsProvider(writeKey);
}
